import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, Link2, Unlink, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

import { api } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

export default function GbpConnectPanel({ businesses = [], onBusinessesRefresh }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [locations, setLocations] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [mapBusy, setMapBusy] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/gbp/status");
      setStatus(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      console.debug("[GBP] status failed:", detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Handle OAuth redirect back
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      toast.success("Google Business Profile collegato");
      window.history.replaceState({}, "", window.location.pathname);
      loadStatus();
    } else if (params.get("google_error")) {
      toast.error(`Errore OAuth: ${params.get("google_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/gbp/connect");
      if (!data.configured) {
        toast.info(data.message || "Integrazione non configurata", { duration: 8000 });
        setConnecting(false);
        return;
      }
      window.location.href = data.auth_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore avvio OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Vuoi davvero scollegare Google? Le recensioni già sincronizzate rimangono, ma non si aggiorneranno più.")) return;
    setDisconnecting(true);
    try {
      await api.post("/gbp/disconnect");
      toast.success("Google scollegato");
      setLocations(null);
      await loadStatus();
    } catch {
      toast.error("Errore durante lo scollegamento");
    } finally {
      setDisconnecting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/gbp/sync");
      toast.success(`Sync completata · ${data.reviews_synced} recensioni su ${data.linked_businesses} attività`);
      await loadStatus();
      onBusinessesRefresh?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante la sync");
    } finally {
      setSyncing(false);
    }
  };

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const { data } = await api.get("/gbp/locations");
      setLocations(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore caricamento location Google");
    } finally {
      setLoadingLocations(false);
    }
  };

  const mapBusinessToLocation = async (business_id, gbp_location) => {
    setMapBusy(business_id);
    try {
      await api.post("/gbp/map", { business_id, gbp_location });
      toast.success("Location collegata. Avvia una sync per portare le recensioni.");
      onBusinessesRefresh?.();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 402 && detail?.resource === "gbp_locations") {
        toast.error(`Piano Free: puoi collegare 1 sola location. Passa a Pro per gestire fino a ${detail.max || 10} location.`, { duration: 6000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore mappatura");
      }
    } finally {
      setMapBusy(null);
    }
  };

  const syncSingle = async (business_id) => {
    setMapBusy(business_id);
    try {
      const { data } = await api.post(`/gbp/sync/${business_id}`);
      toast.success(`Sync ok · ${data.reviews_synced} recensioni per questa sede`);
      onBusinessesRefresh?.();
      await loadStatus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore sync sede");
    } finally {
      setMapBusy(null);
    }
  };

  if (loading) {
    return (
      <div data-testid="gbp-panel-loading" className="rounded-xl border border-dashed border-[var(--border-c)] p-6 text-sm text-[var(--fg-3)]">
        Caricamento stato Google…
      </div>
    );
  }

  if (!status?.configured) {
    return (
      <motion.section
        initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        data-testid="gbp-panel-not-configured"
        className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl p-6 shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-[#E9A94E]/15 text-[#B57F1F] flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={17} strokeWidth={1.7} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">Integrazione Google</div>
            <h3 className="mt-1 font-serif text-xl text-[var(--fg-1)] tracking-tight">Non ancora configurata</h3>
            <p className="mt-2 text-sm text-[var(--fg-3)]">
              L'amministratore deve completare la configurazione OAuth (client_id e client_secret di Google Cloud Console) con lo scope <code>business.manage</code>.
              Finché non è pronta, il dashboard mostra dati demo.
            </p>
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      data-testid="gbp-panel"
      className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl p-6 md:p-8 shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${status.connected ? "bg-[#4A8A6E]/15 text-[#3E7A5E]" : "bg-[var(--bg-3)] text-[var(--fg-2)]"}`}>
            {status.connected ? <CheckCircle2 size={17} strokeWidth={1.7} /> : <Link2 size={17} strokeWidth={1.7} />}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">Google Business Profile</div>
            <h3 className="mt-1 font-serif text-2xl text-[var(--fg-1)] tracking-tight">
              {status.connected ? "Account collegato" : "Collega il tuo account Google"}
            </h3>
            {status.connected && (
              <div className="mt-2 text-xs text-[var(--fg-3)] space-y-0.5">
                {status.google_email && <div>Email: <span className="font-mono text-[var(--fg-2)]">{status.google_email}</span></div>}
                <div>Ultima sync: <span className="text-[var(--fg-2)]">{fmtDate(status.last_sync_at)}</span> · {status.last_sync_reviews || 0} recensioni · {status.last_sync_locations || 0} location</div>
                {status.needs_reconnect && (
                  <div className="text-[#C6423B] font-semibold">Autorizzazione scaduta — ricollega Google.</div>
                )}
              </div>
            )}
            {!status.connected && (
              <p className="mt-2 text-sm text-[var(--fg-3)] max-w-xl">
                Collega Google per sincronizzare in automatico le recensioni della tua attività ogni 6 ore. Nessuna password condivisa: puoi scollegare in qualsiasi momento.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {status.connected ? (
            <>
              <button
                onClick={syncNow}
                disabled={syncing || status.needs_reconnect}
                data-testid="gbp-sync-now-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-5 py-2.5 text-sm font-sans disabled:opacity-50"
                style={{ transition: "background-color 0.2s ease" }}
              >
                {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {syncing ? "Sincronizzo…" : "Sincronizza ora"}
              </button>
              {status.needs_reconnect && (
                <button
                  onClick={connect}
                  disabled={connecting}
                  data-testid="gbp-reconnect-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  <Link2 size={14} /> Ricollega
                </button>
              )}
              <button
                onClick={disconnect}
                disabled={disconnecting}
                data-testid="gbp-disconnect-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#C6423B]/30 text-[#C6423B] hover:bg-[#C6423B]/5 px-4 py-2.5 text-sm font-sans disabled:opacity-50"
                style={{ transition: "background-color 0.2s ease" }}
              >
                <Unlink size={14} /> Scollega
              </button>
            </>
          ) : (
            <button
              onClick={connect}
              disabled={connecting}
              data-testid="gbp-connect-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold disabled:opacity-50"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {connecting ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
              {connecting ? "Reindirizzamento…" : "Collega Google"}
            </button>
          )}
        </div>
      </div>

      {status.connected && businesses.length > 0 && (
        <div className="mt-8 pt-6 border-t border-[var(--border-c)]" data-testid="gbp-map-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">Mappatura location</div>
              <h4 className="mt-1 font-serif text-lg text-[var(--fg-1)] tracking-tight">Collega ogni attività alla sua location Google</h4>
              <p className="mt-1 text-xs text-[var(--fg-3)]">Serve una volta sola: dopo la mappatura, la sync porta le recensioni giuste in ogni dashboard.</p>
            </div>
            <button
              onClick={loadLocations}
              disabled={loadingLocations}
              data-testid="gbp-load-locations-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-4 py-2 text-xs font-sans"
              style={{ transition: "border-color 0.2s ease" }}
            >
              {loadingLocations ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {locations ? "Ricarica location" : "Carica le tue location Google"}
            </button>
          </div>

          <AnimatePresence>
            {locations && (
              <motion.div initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mt-4 space-y-2">
                {locations.locations.length === 0 ? (
                  <div className="text-xs text-[var(--fg-3)] italic">Nessuna location trovata sul tuo account Google (attività non verificate o non gestite da questo account).</div>
                ) : (
                  businesses.map((biz) => {
                    const currentLoc = biz.gbp_location || "";
                    return (
                      <div
                        key={biz.business_id}
                        data-testid={`gbp-map-row-${biz.business_id}`}
                        className="flex items-center gap-3 py-2 border-b border-[var(--border-c)]/60 last:border-b-0"
                      >
                        <div className="flex-1 text-sm text-[var(--fg-1)] font-sans">
                          {biz.name}
                          {biz.address && <span className="text-xs text-[var(--fg-3)] ml-2">· {biz.address}</span>}
                        </div>
                        <select
                          data-testid={`gbp-location-select-${biz.business_id}`}
                          value={currentLoc}
                          onChange={(e) => e.target.value && mapBusinessToLocation(biz.business_id, e.target.value)}
                          disabled={mapBusy === biz.business_id}
                          className="rounded-lg border border-[var(--border-c)] bg-[var(--bg-1)] px-3 py-1.5 text-xs text-[var(--fg-1)] font-sans min-w-[240px]"
                        >
                          <option value="">— Nessuna location —</option>
                          {locations.locations.map((loc) => (
                            <option key={loc.name} value={loc.name}>
                              {loc.title}{loc.store_code ? ` (${loc.store_code})` : ""}
                            </option>
                          ))}
                        </select>
                        {currentLoc && (
                          <button
                            onClick={() => syncSingle(biz.business_id)}
                            disabled={mapBusy === biz.business_id}
                            data-testid={`gbp-sync-single-${biz.business_id}`}
                            title="Sincronizza solo questa sede"
                            className="p-1.5 rounded-full border border-[var(--border-c)] hover:border-[#00F2F4] hover:text-[#00F2F4] text-[var(--fg-2)]"
                            style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
                          >
                            <RefreshCw size={12} />
                          </button>
                        )}
                        {mapBusy === biz.business_id && <Loader2 size={12} className="animate-spin text-[var(--fg-3)]" />}
                      </div>
                    );
                  })
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.section>
  );
}
